import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authAPI, type LoginRequest, type LoginResponse, type UserInfo } from '@/services/api'
import { authLog, authError, authLogToken } from '@/utils/logger'

export interface AuthState {
  imapJwt: string | null
  user: UserInfo | null
  isLoading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const imapJwt = ref<string | null>(null)
  const user = ref<UserInfo | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!imapJwt.value)
  const isLoggedIn = computed(() => isAuthenticated.value && !!user.value)

  // Initialize store from localStorage
  const initializeAuth = () => {
    const storedAccessToken = localStorage.getItem('access_token')
    const storedImapJwt = localStorage.getItem('imap_jwt')
    const storedUser = localStorage.getItem('user_info')

    // Only restore auth state if we have both token and IMAP JWT
    if (storedAccessToken && storedImapJwt) {
      imapJwt.value = storedImapJwt
      authLog('Auth state restored from localStorage')
      authLogToken('access', storedAccessToken)
      authLogToken('refresh', localStorage.getItem('refresh_token'))
    }
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser)
        // Ensure username is set (fallback to user_id for backwards compatibility)
        // This handles localStorage from before username was added to the stored object
        if (!parsed.username && parsed.user_id) {
          parsed.username = parsed.user_id
        }
        user.value = parsed
        authLog('User info restored', { user_id: user.value?.user_id })
      } catch (e) {
        authError('Failed to parse stored user info', e)
        clearAuth()
      }
    }
  }

  // Verify tokens are stored in localStorage
  const verifyTokensStored = (): boolean => {
    authLog('Verifying tokens in localStorage...')

    try {
      const accessToken = localStorage.getItem('access_token')
      const refreshToken = localStorage.getItem('refresh_token')
      const imapJwtToken = localStorage.getItem('imap_jwt')
      const userInfo = localStorage.getItem('user_info')

      authLogToken('access', accessToken)
      authLogToken('refresh', refreshToken)
      authLogToken('imap_jwt', imapJwtToken)

      if (!accessToken || !refreshToken) {
        authError('Tokens not found in localStorage after storage', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          hasImapJwt: !!imapJwtToken,
          hasUserInfo: !!userInfo
        })
        return false
      }

      authLog('Tokens verified successfully in localStorage')
      return true
    } catch (error) {
      authError('Error accessing localStorage', error)
      return false
    }
  }

  // Actions
  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      isLoading.value = true
      error.value = null

      authLog('Starting login process...', { username: credentials.username, server: credentials.server })

      // Call UI Service via authAPI.login()
      const response: LoginResponse = await authAPI.login(credentials)

      authLog('Login API call successful', { user_id: response.user_id, session_id: response.session_id })

      // Store tokens and user info
      imapJwt.value = response.imap_jwt
      user.value = {
        user_id: response.user_id,
        username: response.user_id,
        server: response.server_info?.server || credentials.server,
        port: response.server_info?.port || credentials.port,
        use_ssl: response.server_info?.use_ssl || credentials.use_ssl,
        imap_jwt: response.imap_jwt,
        session_id: response.session_id,
        created_at: new Date().toISOString(),
        last_accessed: new Date().toISOString(),
        expires_at: new Date(Date.now() + response.expires_in * 1000).toISOString()
      }

      // Persist to localStorage (authAPI.login already handles this)
      authLog('Storing user info to localStorage...')
      localStorage.setItem('user_info', JSON.stringify(user.value))

      // Verify tokens are stored before proceeding
      if (!verifyTokensStored()) {
        throw new Error('Token storage verification failed. Please try logging in again.')
      }

      authLog('Login completed successfully, tokens verified')

      // Note: Notification permission is NOT requested automatically on login.
      // Users must explicitly enable notifications in Preferences page.
      // This prevents intrusive permission popups blocking the email list view.

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Login failed'
      error.value = errorMessage
      authError('Login failed', { error: errorMessage, response: err.response?.data })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      isLoading.value = true
      error.value = null

      if (imapJwt.value) {
        // Call UI Service via authAPI.logout()
        await authAPI.logout({})
      }
    } catch (err: any) {
      console.error('Logout error:', err)
      // Continue with logout even if API call fails
    } finally {
      // Clear state regardless of API call success
      clearAuth()
      isLoading.value = false
    }
  }

  const clearAuth = () => {
    authLog('Clearing authentication state...')

    imapJwt.value = null
    user.value = null
    error.value = null

    // Clear localStorage
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('imap_jwt')
    localStorage.removeItem('user_info')

    authLog('Authentication state cleared')
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  // Initialize auth state on store creation
  initializeAuth()

  return {
    // State
    imapJwt,
    user,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    isLoggedIn,

    // Actions
    login,
    logout,
    clearAuth,
    setError,
    clearError,
    initializeAuth,
    verifyTokensStored
  }
})